import { Box, List, ListItem, ListItemText, Typography } from '@mui/material';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';

import { uploadFileWithProgress } from '@actions/file';

import { DrilldownHeader } from '@components/DrilldownHeader';
import FileDropzone from '@components/FileDropzone/FileDropzone';
import GroupName from '@components/GroupName/GroupName';
import Icon from '@components/Icon/Icon';
import TextInputBox from '@components/TextInputBox';

import { isDevelopment } from '@constants/env';
import * as ROUTES from '@constants/routes';

import { useGetAccount } from '@hooks/api/account';
import {
  useSetDocumentsUploaded,
  useUploadImportJobCsv,
} from '@hooks/api/document';
import { useCreateUserImportJob } from '@hooks/api/user';
import { useSnackbar } from '@hooks/useSnackbar';

import AppLayout from '@layouts/AppLayout';
import { MainContainer, PageBody } from '@layouts/DrilldownStyle';

import { toBase64 } from '@utils/toBase64';

import type { UserImportJob } from 'generated/types';

import { FileDrop } from './ImportJobForm.style';

const messages = defineMessages({
  title: {
    id: 'importJobForm.title',
    defaultMessage: 'Import Users',
  },
  jobName: {
    id: 'importJobForm.jobName',
    defaultMessage: 'Job Name',
  },
  groupName: {
    id: 'importJobForm.groupName',
    defaultMessage: 'Group Name',
  },
  uploadLabel: {
    id: 'importJobForm.uploadLabel',
    defaultMessage: 'Drop your csv here or click to upload',
  },
  instructions: {
    id: 'importJobForm.instructions',
    defaultMessage:
      'Enter a name for the user import job and attach a csv file',
  },
  error: {
    id: 'GLOBAL.error',
    defaultMessage: 'An error occurred. Please try again later.',
  },
  started: {
    id: 'importJobForm.started',
    defaultMessage: 'Import Job Has Started',
  },
  importedUsers: {
    id: 'importJobForm.importedUsers',
    defaultMessage: '# of Users Created in Database: ',
  },
  skippedUsers: {
    id: 'importJobForm.skippedUsers',
    defaultMessage: '# of Skipped Users: ',
  },
  failedUsers: {
    id: 'importJobForm.failedUsers',
    defaultMessage: '# of Failed Users: ',
  },
  accounts: {
    id: 'importJobForm.accounts',
    defaultMessage: '# of Accounts Created: ',
  },
  filename: {
    id: 'importJobForm.filename',
    defaultMessage: 'Uploaded Filename: ',
  },
});

const ItemText: React.FC<{ text: string }> = ({ text }) => (
  <ListItem>
    <ListItemText
      primary={text}
      primaryTypographyProps={{
        variant: 'subtitle1',
      }}
    />
  </ListItem>
);

const ImportJobForm: React.FC = () => {
  const { formatMessage } = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const parentId = useSearchParam('parent_id');

  const [names, setNames] = useState({
    group: '',
    job: '',
    file: '',
  });
  const [response, setResponse] = useState<UserImportJob | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setNames((prev) => ({ ...prev, job: value }));
  };

  const { data, loading } = useGetAccount({
    variables: { input: { account_id: parentId ?? '' } },
    skip: !parentId,
  });

  const [createImportJob] = useCreateUserImportJob();
  const [uploadCsv] = useUploadImportJobCsv();
  const [setDocumentsUploaded] = useSetDocumentsUploaded();

  const handleSubmit = async (files: DroppedFile[]) => {
    try {
      if (files.length < 1) {
        throw new Error('No file attached');
      }
      const [file] = files;
      setNames((prev) => ({ ...prev, file: file.name }));

      // Import users
      const base64File = await toBase64(file);
      if (!base64File) {
        throw new Error('No file attached');
      }
      const { data: importJobData } = await createImportJob({
        input: {
          jobName: names.job,
          groupName: names.group,
          base64File,
          parentAccountId: parentId,
        },
        groupName: names.group,
      });
      const importJob = importJobData?.postUserImportJob;
      if (!importJob) {
        throw new Error('An error occurred while attempting to import users');
      }
      const { JobId, GroupId } = importJob;

      // Save uploaded csv
      const { data: uploadCsvData } = await uploadCsv({
        input: {
          job_id: JobId,
          group_id: GroupId,
          document: {
            name: file.name,
            description: `Import Job ${JobId} for group ${GroupId}`,
            size: file.size,
          },
        },
      });
      const response = uploadCsvData?.uploadImportJobCsv;
      if (!response || !response.document_id) {
        throw new Error('Failed to upload csv to database');
      }
      const { document_id, uploadData } = response;
      const { isMultipart, multipartUploadId, parts, expectedHeaders } =
        uploadData;

      const uploadedParts = await uploadFileWithProgress(
        file,
        parts,
        undefined,
        expectedHeaders
      );

      await setDocumentsUploaded({
        input: {
          documents: [
            {
              documentId: document_id,
              multipartUploadInfo: {
                isMultipart,
                parts:
                  isMultipart && uploadedParts instanceof Array
                    ? uploadedParts
                    : [],
                multipartUploadId,
              },
            },
          ],
        },
      });
      setResponse({ ...importJob, AccountsInGroup: 0 });
    } catch (e) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
      enqueueSnackbar({
        message: formatMessage(messages.error),
        variant: 'error',
      });
    }
  };

  return (
    <AppLayout loading={loading} showHeader>
      <DrilldownHeader
        backRoute={ROUTES.ADMIN_NEW_ACCOUNT}
        subtitle={data?.getAccountRecord.account_name}
        title={formatMessage(messages.title)}
      />
      <MainContainer maxWidth="600px">
        {response ? (
          <PageBody>
            <Box sx={{ textAlign: 'center', width: '100%' }}>
              <Icon color="success.main" name="circle-check" size="3x" />
              <h3>{formatMessage(messages.started)}</h3>

              <List dense>
                <ItemText
                  text={`${formatMessage(messages.filename)}${names.file}`}
                />
                <ItemText text={`Status: ${response.Status}`} />
                <ItemText
                  text={`${formatMessage(messages.importedUsers)}${
                    response.ImportedUsers
                  }`}
                />
                <ItemText
                  text={`${formatMessage(messages.skippedUsers)}${
                    response.SkippedUsers
                  }`}
                />
                {/* <ItemText
                  text={`${formatMessage(messages.failedUsers)}${
                    response.FailedUsers
                  }`}
                /> */}
                <ItemText
                  text={`${formatMessage(messages.accounts)}${
                    response.ImportedAccounts
                  }`}
                />
                <ItemText
                  text={`${formatMessage(messages.groupName)}: ${names.group}`}
                />
              </List>
            </Box>
          </PageBody>
        ) : (
          <PageBody>
            <Typography
              sx={{
                fontSize: '16px',
                mb: '15px',
              }}
            >
              {formatMessage(messages.instructions)}
            </Typography>
            <TextInputBox
              label={formatMessage(messages.jobName)}
              onChange={handleChange}
              required
              value={names.job}
              width="100%"
            />

            <GroupName
              formView
              onSelectionChange={(value) => {
                setNames((prev) => ({
                  ...prev,
                  group: value,
                }));
              }}
              onTypeChange={(value) =>
                setNames((prev) => ({ ...prev, group: value }))
              }
              value={{
                group_name: names.group,
                group_id: '',
              }}
            />

            <FileDrop>
              <FileDropzone
                accept={{
                  'text/csv': ['.csv'],
                }}
                label={formatMessage(messages.uploadLabel)}
                maxFiles={1}
                onUpload={handleSubmit}
              />
            </FileDrop>
          </PageBody>
        )}
      </MainContainer>
    </AppLayout>
  );
};

export default ImportJobForm;
